import { useTheme } from "@mui/material";
import { ErrorBoundary, ErrorPage, LoadingPage, Shell } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

import { AutostoreStatusInfo } from "~/components/debug/AutostoreStatusInfo";
import EventSimulator from "~/components/debug/EventSimulator";
import envConstants from "~/config/envConstants";
import { Navbar } from "~/features/navbar/Navbar";
import { NavigationDrawer } from "~/features/navigationDrawer/NavigationDrawer";
import useCloseWorkstationOnLocationChange from "~/hooks/useCloseWorkstationOnLocationChange";
import { DevCheatsContext } from "~/hooks/useDevCheats";
import { AutostoreInactivityResetTimerProvider } from "~/hooks/useInactivityResetTimer";
import { useUnifyConnect } from "~/hooks/useUnifyConnect";
import { getStatusCodeFromError } from "~/lib/getStatusCodeFromError";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useGetAutostoreGridQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import { GridConnection } from "./GridConnection";
import { useAppSelector } from "./store";

/**
 * Renders Autostore pages.
 *
 * Fetches workstations and throws an error if fetching fails.
 * @constructor
 */
export function AutostoreLayout() {
  const { isPortPolling, showAutostoreStatus, showEventSimulator } =
    useContext(DevCheatsContext);

  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const [isAutostoreStatusOpen, setIsAutostoreStatusOpen] = useState(false);
  const [isEventSimulatorOpen, setIsEventSimulatorOpen] = useState(false);
  const userHasAnFc = !!useAppSelector(selectUsersFulfillmentCenter);
  const selectedAutostoreGridId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.autostoreGridId
  );

  useGetAutostoreGridQuery(
    !selectedAutostoreGridId ? skipToken : selectedAutostoreGridId
  );

  const { isLoading: isGetWorkstationsLoading, error: getWorkstationsError } =
    useGetWorkstationsQuery(undefined, {
      skip: !userHasAnFc
    });

  useCloseWorkstationOnLocationChange();
  useUnifyConnect();

  const devCheatClickHandler = useCallback(() => {
    if (envConstants.DEV_CHEATS_UAT !== "true") {
      return;
    }
    if (showAutostoreStatus) {
      setIsAutostoreStatusOpen(!isAutostoreStatusOpen);
    }
    if (showEventSimulator) {
      setIsEventSimulatorOpen(!isEventSimulatorOpen);
    }
  }, [
    isAutostoreStatusOpen,
    isEventSimulatorOpen,
    showAutostoreStatus,
    showEventSimulator
  ]);

  return (
    <Shell
      AppBar={<Navbar onDevCheatClick={devCheatClickHandler} />}
      Drawer={<NavigationDrawer />}
      sx={{
        "& main": {
          minWidth: 0,
          overflowX: "auto",
          [breakpoints.up("xl")]: {
            //Hide scrollbar but keep functionality
            //default screen dimension only
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none"
            }
          },
          [breakpoints.down("xl")]: {
            scrollbarWidth: "thin",
            msOverflowStyle: "auto",
            "&::-webkit-scrollbar": {
              width: "6px"
            }
          }
        }
      }}
    >
      <ErrorBoundary>
        {isGetWorkstationsLoading && (
          <LoadingPage loadingMessage={t("fetching workstations")} />
        )}
        {!!getWorkstationsError && (
          <ErrorPage
            errorMessage={getMessageFromRtkError(getWorkstationsError)}
            errorCode={getStatusCodeFromError(getWorkstationsError)}
          />
        )}
        {!isGetWorkstationsLoading && !getWorkstationsError && (
          <AutostoreInactivityResetTimerProvider>
            <GridConnection />
            <Outlet />
          </AutostoreInactivityResetTimerProvider>
        )}
        {isAutostoreStatusOpen && (
          <AutostoreStatusInfo
            defaultAccordion="bin present"
            portPollingIsActive={isPortPolling}
            floating
          />
        )}
        {isEventSimulatorOpen && <EventSimulator />}
      </ErrorBoundary>
    </Shell>
  );
}
